import type {DocumentNode, OperationDefinitionNode} from "graphql"
import {ErrorHandlerCallback} from "@irati/utils/services/API/utils/errorHandler/register"
import {ApolloError} from "@apollo/client"

// this is the type as created by our error handler, see the apollo.client logic
export declare class PBApolloError extends ApolloError {
  override networkError: GraphError
}

export type {DocumentNode}
export type ServiceOptions = {
  /**
   * The name of the root value on the return type, only applicable if using one root node
   * @example
   * const query = gql`{
   *   user {
   *     name
   *     email
   *   }
   * }`
   *
   * const useUser = createUserQuery(query, { key: 'user' }) // returns { name: 'blah', email: 'foo@blah' }
   */
  key?: string
  /**
   * a list of error handlers that can map error codes from the downstream call into a key for our CMS
   */
  errorHandlers?: ErrorHandlerCallback[]
}

const failedPromiseCache = new WeakMap<PBApolloError, Promise<never>>()

const pendingState = {
  pending: new Promise<void>(() => {}),
  complete: Promise.resolve(),
  failed(error: PBApolloError) {
    // must return the SAME promise instance for the same error, otherwise consumers like
    // <Loading /> that compare `deriveLoad(load)` by reference across renders (componentDidUpdate)
    // see a "new" promise every render and re-trigger their error handling in an infinite loop
    let promise = failedPromiseCache.get(error)
    if (!promise) {
      promise = Promise.reject(error)
      failedPromiseCache.set(error, promise)
    }
    return promise
  }
}

/**
 * This branded type is to help interop with our useQuery/useMutation hooks and our `Loading` and `Submit` base components.
 *
 * They take in promises, but returning promises that can throw from hooks is
 * error prone, so this special type is then understood by those components, and converted where needed
 */
export type ApolloLoad = {
  __brand: "ApolloLoad"
  loading?: boolean
  error?: PBApolloError
  data?: any
}

/**
 * This branded type is to help interop with our useQuery/useMutation hooks and our `Loading` and `Submit` base components.
 *
 * They take in promises, but returning promises that can throw from hooks is
 * error prone, so this special type is then understood by those components, and converted where needed
 */
export function deriveLoad<A extends Promise<any> | null | undefined>(
  load: A | ApolloLoad | ApolloLoad[]
): Promise<void> | null | undefined {
  if (isApolloLoad(load)) {
    if (Array.isArray(load)) {
      // check errors first
      const anError = load.find((item): item is WithRequired<ApolloLoad, "error"> => !!item.error)
      if (anError) return pendingState.failed(anError.error)

      // any loading is considered all loading
      const aLoading = load.find((item) => item.loading && item.data === undefined)
      if (aLoading) return pendingState.pending

      return pendingState.complete
    } else {
      const {error, loading, data} = load
      // the loading && data === undefined is to prevent the loading spinner from showing while the data is refetched in the background, inline with our existing Loading component. If a dev wants finer control, they should use the Loader and Error components directly
      return loading && data === undefined ? pendingState.pending : error ? pendingState.failed(error) : pendingState.complete
    }
  }

  return load
}

export function isApolloLoad(x: unknown): x is ApolloLoad | ApolloLoad[] {
  if (!x) return false
  if (Array.isArray(x) && x.every((i) => isApolloLoad(i))) return true
  return typeof x === "object" && "__brand" in x && x.__brand === "ApolloLoad"
}

/**
 * give our created hook a name to show up in react devtools. MUTATES the passed in function
 * e.g something like:
 * ```
 * query Deals { ... }
 * ```
 * becomes `useDealsQuery` (falls back to `useAnonymousOperation`)
 */
export function deriveHookNameAndRenameFunctionForDevtools<Fn extends (...args: any[]) => any>(
  fn: Fn,
  type: "Query" | "Mutation",
  query: DocumentNode
): Fn {
  const def = query.definitions.find((d): d is OperationDefinitionNode => d.kind === "OperationDefinition")
  const nameValue = def?.name?.value
  if (!nameValue) throw new Error("Operation must be named")
  const name = `use${nameValue}${type}`

  Object.defineProperty(fn, "name", {value: name, writable: false})
  return fn
}
